import React, { useEffect, useState } from 'react';
import FormControl from '../../common/FormControl';
import { UserCheck } from 'lucide-react';
import { litOrgService, type PositionLookup, type PickablePosition } from '../../../services/litInstanceService';

interface Props {
  positionId: number | null;
  /** Fires with the picked position mapped to the PositionLookup shape (or null on clear). */
  onPositionChange: (pos: PositionLookup | null) => void;
}

const occupantOf = (p: PickablePosition): string | null =>
  (p.occupant_name || '').trim() || null;

const toLookup = (p: PickablePosition): PositionLookup => ({
  id: p.id,
  name: p.name,
  code: null,
  authority_name: p.authority_name,
  location_name: p.location_name,
  user_id: p.occupant_user_id,
  occupant_name: occupantOf(p),
});

/** Direct position picker: one dropdown listing every active position with its
 *  authority, location and current occupant — the user picks the exact seat
 *  (an authority can own several positions, so authority+location is not
 *  enough to identify one person). */
const LitPositionPicker: React.FC<Props> = ({ positionId, onPositionChange }) => {
  const [positions, setPositions] = useState<PickablePosition[]>([]);
  const [loadErr, setLoadErr] = useState<string | null>(null);

  // Surface load failures instead of swallowing them — a silent catch makes a
  // failed fetch look identical to a "loaded but empty" dropdown.
  useEffect(() => {
    litOrgService.getPickablePositions()
      .then(setPositions)
      .catch(err => setLoadErr(`Couldn't load positions (${err?.response?.status ?? err?.message ?? 'error'}).`));
  }, []);

  const selected = positions.find(p => p.id === positionId) ?? null;

  return (
    <div className="flex flex-col gap-y-3">
      {loadErr && <p className="text-xs text-red-600">{loadErr}</p>}
      <FormControl.SearchSelect
        label="Position"
        placeholder="— Select position —"
        clearable
        value={positionId ?? null}
        onChange={v => {
          const pos = v ? positions.find(p => p.id === Number(v)) ?? null : null;
          onPositionChange(pos ? toLookup(pos) : null);
        }}
        options={positions.map(p => ({
          value: p.id,
          label: `${p.name ?? `Position #${p.id}`}${p.location_name ? ` (${p.location_name})` : ''}`,
        }))}
        data-testid="SEL_lit_inst_position"
      />

      {/* Selected position + occupant */}
      {selected && (
        <div className="flex items-start gap-2.5 p-3 rounded-xl border text-sm bg-emerald-50 border-emerald-200">
          <UserCheck size={14} className="text-emerald-600 mt-0.5 shrink-0" />
          <div>
            <p className="text-xs font-semibold text-emerald-800">{selected.name ?? `Position #${selected.id}`}</p>
            <p className="text-[11px] text-emerald-700 mt-0.5">
              {selected.authority_name ?? '—'} · {selected.location_name ?? '—'}
            </p>
            <p className="text-[11px] text-gray-600 mt-1">
              <span className="text-gray-400">Assigned to: </span>
              {occupantOf(selected)
                ? <span className="font-medium">{occupantOf(selected)}</span>
                : <span className="italic text-gray-400">Position vacant</span>}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default LitPositionPicker;
